/**
 * Switch Core - Cross-platform crypto, IDs, and schema validation
 *
 * This module provides a single source of truth for types and validation
 * across the Switch application, powered by Rust WASM.
 */

import init, * as wasm from "./switch_core";

// Import types needed for function signatures
import type { Thread as ThreadType, EncryptedThread as EncryptedThreadType, HandleResult } from "./types";

// Re-export all types from the types file
export type {
  User,
  UserForClient,
  UserType,
  PublicKeyEntry,
  ParsedHandle,
  HandlerAction,
  HandleResult,
  Contact,
  AddContactResult,
  Member,
  MemberRole,
  MembershipStatus,
  MembershipStatusType,
  Message,
  MessageTypeEnum,
  Attachment,
  Thread,
  HiloMeta,
  EncryptedThread,
  Community,
  CommunityGroup,
  EncryptedCommunity,
  Token,
  TokenType,
  PushToken,
  DebugAppScreen,
  GroupUpdatedMessageContent,
  CommunityUpdatedMessageContent,
} from "./types";
export type { ProfileMediaAsset } from "./types";

// Re-export enum constants (for runtime access)
export {
  ContentType,
  MessageType,
  MessageTypeHelper,
  MessageStatus,
  AttachmentMimeType,
  Environment,
  ThreadType,
  GroupUpdateReason,
  CommunityUpdateReason,
  PushPlatform,
  WebSocketEventType,
} from "./types";

// Initialization state
let initialized = false;
let initPromise: Promise<void> | null = null;

/**
 * Initialize the WASM module. Call this once at app startup.
 * Safe to call multiple times - will only initialize once.
 */
export async function initSwitchCore(): Promise<void> {
  if (initialized) return;
  if (initPromise) return initPromise;

  initPromise = init().then(() => {
    initialized = true;
    // Version info is logged by the wasm init() function
  });

  return initPromise;
}

// ============================================================================
// Version Info - Build-time injected from core/build.rs
// ============================================================================

/** Get library version from Cargo.toml (e.g., "0.1.0") */
export function getVersion(): string {
  return wasm.version();
}

/** Get git commit hash (short, 7 chars) */
export function getGitHash(): string {
  return wasm.git_hash();
}

/** Get git describe (tag + commits, or just hash) */
export function getGitDescribe(): string {
  return wasm.git_describe();
}

/** Get build date (YYYY-MM-DD) */
export function getBuildDate(): string {
  return wasm.build_date();
}

/** Get full build info string (e.g., "0.1.0 (abc1234) - 2024-01-15") */
export function getBuildInfo(): string {
  return wasm.build_info();
}

export function linkedDeviceFieldKind(): string {
  return wasm.linked_device_field_kind();
}

export function linkedDeviceNotificationKind(): string {
  return wasm.linked_device_notification_kind();
}

export function linkedDeviceKeySyncKind(): string {
  return wasm.linked_device_key_sync_kind();
}

export function linkedDeviceFieldDeviceId(): string {
  return wasm.linked_device_field_device_id();
}

export function linkedDeviceFieldTempPublicKey(): string {
  return wasm.linked_device_field_temp_public_key();
}

export function linkedDeviceFieldPublicKey(): string {
  return wasm.linked_device_field_public_key();
}

export function linkedDeviceFieldSecretKey(): string {
  return wasm.linked_device_field_secret_key();
}

export function linkedDeviceFieldMlKemPublicKey(): string {
  return wasm.linked_device_field_ml_kem_public_key();
}

export function linkedDeviceFieldMlKemSecretKey(): string {
  return wasm.linked_device_field_ml_kem_secret_key();
}

export function linkedDeviceFieldTargetDeviceId(): string {
  return wasm.linked_device_field_target_device_id();
}

/**
 * Check if WASM is initialized
 */
export function isInitialized(): boolean {
  return initialized;
}

// ============================================================================
// Validation Functions - throw on invalid data
// ============================================================================

import type {
  User,
  UserForClient,
  Contact,
  Message,
  Thread,
  Community,
  Token,
  PushToken,
  Member,
} from "./types";

export function validateUser(data: unknown): User {
  return wasm.validate_user(JSON.stringify(data));
}

export function validateUserForClient(data: unknown): UserForClient {
  return wasm.validate_user_for_client(JSON.stringify(data));
}

export function validateMessage(data: unknown): Message {
  return wasm.validate_message(JSON.stringify(data));
}

export function validateThread(data: unknown): Thread {
  return wasm.validate_thread(JSON.stringify(data));
}

export function validateCommunity(data: unknown): Community {
  return wasm.validate_community(JSON.stringify(data));
}

export function validateToken(data: unknown): Token {
  return wasm.validate_token(JSON.stringify(data));
}

export function validatePushToken(data: unknown): PushToken {
  return wasm.validate_push_token(JSON.stringify(data));
}

export function validateMember(data: unknown): Member {
  return wasm.validate_member(JSON.stringify(data));
}

// ============================================================================
// Safe Validation - returns { success, data, error } instead of throwing
// ============================================================================

type SafeResult<T> =
  | { success: true; data: T; error?: never }
  | { success: false; data?: never; error: string };

export function safeValidateUser(data: unknown): SafeResult<User> {
  try {
    return { success: true, data: validateUser(data) };
  } catch (e) {
    return { success: false, error: String(e) };
  }
}

export function safeValidateUserForClient(data: unknown): SafeResult<UserForClient> {
  try {
    return { success: true, data: validateUserForClient(data) };
  } catch (e) {
    return { success: false, error: String(e) };
  }
}

export function safeValidateMessage(data: unknown): SafeResult<Message> {
  try {
    return { success: true, data: validateMessage(data) };
  } catch (e) {
    return { success: false, error: String(e) };
  }
}

export function safeValidateThread(data: unknown): SafeResult<Thread> {
  try {
    return { success: true, data: validateThread(data) };
  } catch (e) {
    return { success: false, error: String(e) };
  }
}

export function safeValidateCommunity(data: unknown): SafeResult<Community> {
  try {
    return { success: true, data: validateCommunity(data) };
  } catch (e) {
    return { success: false, error: String(e) };
  }
}

// ============================================================================
// Parse Functions - convert JSON string to typed object
// ============================================================================

export function parseUser(json: string): User {
  return wasm.parse_user(json);
}

export function parseUserForClient(json: string): UserForClient {
  return wasm.parse_user_for_client(json);
}

export function parseMessage(json: string): Message {
  return wasm.parse_message(json);
}

/**
 * Build the versioned content envelope plaintext to encrypt for a message.
 *
 * `contentType` is the wire content type (e.g. "TEXT", "FILE", "GIPHY"); core
 * derives the envelope kind and strips the environment for control messages.
 * `environment` is the current HiLo environment ("hi"/"lo") or null.
 *
 * @platform-only: thin WASM binding to core `build_message_content_for_send`
 * (core/src/messaging.rs). No logic here — mirrors `parseMessage` above.
 */
export function buildMessageContent(
  content: string,
  contentType: string,
  environment?: string | null
): string {
  return wasm.build_message_content(content, contentType, environment ?? null);
}

/**
 * Parse decrypted plaintext into `{ content, kind, environment }`.
 *
 * Recognizes the versioned content envelope and falls back to legacy raw-text
 * or bare-attachment-JSON content. `kind` is "text" | "attachment";
 * `environment` is "hi" | "lo" | null.
 *
 * @platform-only: thin WASM binding to core `parse_message_content`
 * (core/src/messaging.rs). No logic here — mirrors `parseMessage` above.
 */
export function parseMessageContent(decrypted: string): {
  content: string;
  kind: "text" | "attachment";
  environment: string | null;
} {
  return wasm.parse_message_content(decrypted);
}

/** Handle a message JSON via core handler logic */
export function handleMessageJson(messageJson: string, currentUserId: string): HandleResult {
  return wasm.handle_message_json(messageJson, currentUserId);
}

export function handleIncomingMessageJson(
  payloadJson: string,
  currentUserId: string,
  receiverSecretKeyBase64: string,
  senderPublicKeyBase64?: string | null
): { message: Message; handleResult: HandleResult; decrypted: boolean; error?: string | null } {
  return wasm.handle_incoming_message_json(
    payloadJson,
    currentUserId,
    receiverSecretKeyBase64,
    senderPublicKeyBase64 ?? null
  );
}

export function handleIncomingMessageJsonForDevice(
  payloadJson: string,
  currentUserId: string,
  receiverSecretKeyBase64: string,
  receiverDeviceId?: string | null,
  receiverMlKemPublicKeyBase64?: string | null,
  receiverMlKemSecretKeyBase64?: string | null,
  senderPublicKeyBase64?: string | null,
  senderMlKemPublicKeyBase64?: string | null
): { message: Message; handleResult: HandleResult; decrypted: boolean; error?: string | null } {
  return wasm.handle_incoming_message_json_for_device(
    payloadJson,
    currentUserId,
    receiverSecretKeyBase64,
    receiverDeviceId ?? null,
    receiverMlKemPublicKeyBase64 ?? null,
    receiverMlKemSecretKeyBase64 ?? null,
    senderPublicKeyBase64 ?? null,
    senderMlKemPublicKeyBase64 ?? null
  );
}

export function parseThread(json: string): Thread {
  return wasm.parse_thread(json);
}

export function parseCommunity(json: string): Community {
  return wasm.parse_community(json);
}

export function parseToken(json: string): Token {
  return wasm.parse_token(json);
}

export function parseMember(json: string): Member {
  return wasm.parse_member(json);
}

// Convenience re-export for cache path helper
export function displayPhotoCachePaths(userId: string, cleanUrl: string) {
  return api.displayPhotoCachePaths(userId, cleanUrl);
}

export function prepareDisplayPhotoFanout(
  userId: string,
  displayPhotoUrl: string,
  displayPhotoKey: string,
  receiverId: string,
  receiverPublicKeyBase64: string,
  myUserId: string,
  mySecretKeyBase64: string
) {
  return api.prepareDisplayPhotoFanout(
    userId,
    displayPhotoUrl,
    displayPhotoKey,
    receiverId,
    receiverPublicKeyBase64,
    myUserId,
    mySecretKeyBase64
  );
}

export function parseDisplayPhotoUpdate(content: string) {
  return api.parseDisplayPhotoUpdate(content);
}

// ============================================================================
// Stringify Functions - convert object to JSON string
// ============================================================================

export function stringifyUser(user: User): string {
  return wasm.stringify_user(user);
}

export function stringifyUserForClient(user: UserForClient): string {
  // UserForClient extends User, so we can use the same stringify function
  return wasm.stringify_user(user);
}

export function stringifyMessage(message: Message): string {
  return wasm.stringify_message(message);
}

export function stringifyThread(thread: Thread): string {
  return wasm.stringify_thread(thread);
}

export function stringifyCommunity(community: Community): string {
  return wasm.stringify_community(community);
}

// ============================================================================
// Handle Functions - for username.discriminator format
// ============================================================================

export function parseHandle(handle: string): { username: string; discriminator: string } | null {
  return wasm.parse_handle(handle);
}

export function formatHandle(username: string, discriminator: string): string {
  return wasm.create_handle(username, discriminator);
}

/**
 * Validate a username
 * @returns null if valid, error message string if invalid
 */
export function validateUsername(username: string): string | null {
  const result = wasm.validate_username(username);
  return result === null ? null : result;
}

/**
 * Validate a discriminator (4-digit number)
 * @returns null if valid, error message string if invalid
 */
export function validateDiscriminator(discriminator: string): string | null {
  const result = wasm.validate_discriminator(discriminator);
  return result === null ? null : result;
}

// ============================================================================
// ID Generation Functions
// ============================================================================

export function generateUUID(): string {
  if (!initialized) {
    // Fallback to native crypto.randomUUID when WASM not ready
    return globalThis.crypto.randomUUID();
  }
  return wasm.generate_uuid();
}

export function generateRandomId(): string {
  return wasm.generate_random_id();
}

export function generateInviteCode(length?: number): string {
  return wasm.generate_invite_code(length);
}

export function createThreadId(userId1: string, userId2: string): string {
  return wasm.create_thread_id(userId1, userId2);
}

export interface ConversationLaneIdentity {
  conversationId: string;
  laneId: string;
  conversationKind: string;
  environmentId: string;
  environmentChannelId: string;
  accessMode: string;
}

export interface ConversationProjection {
  visible: boolean;
  reason: string;
  conversationKind: string;
}

function parseCoreJson<T>(value: string): T {
  return JSON.parse(value) as T;
}

export function createDmConversationId(userIdA: string, userIdB: string): string {
  return wasm.create_dm_conversation_id(userIdA, userIdB);
}

export function createSelfConversationId(userId: string): string {
  return wasm.create_self_conversation_id(userId);
}

export function createGroupConversationId(groupId: string): string {
  return wasm.create_group_conversation_id(groupId);
}

export function createCommunityChannelConversationId(
  communityId: string,
  communityChannelId: string
): string {
  return wasm.create_community_channel_conversation_id(communityId, communityChannelId);
}

export function createDmConversationLane(
  userIdA: string,
  userIdB: string,
  environmentId: string,
  environmentChannelId: string,
  accessMode?: string | null
): ConversationLaneIdentity {
  return parseCoreJson<ConversationLaneIdentity>(
    wasm.create_dm_conversation_lane_json(
      userIdA,
      userIdB,
      environmentId,
      environmentChannelId,
      accessMode ?? undefined
    )
  );
}

export function createSelfConversationLane(
  userId: string,
  environmentId: string,
  environmentChannelId: string,
  accessMode?: string | null
): ConversationLaneIdentity {
  return parseCoreJson<ConversationLaneIdentity>(
    wasm.create_self_conversation_lane_json(
      userId,
      environmentId,
      environmentChannelId,
      accessMode ?? undefined
    )
  );
}

export function createGroupConversationLane(
  groupId: string,
  environmentId: string,
  environmentChannelId: string,
  accessMode?: string | null
): ConversationLaneIdentity {
  return parseCoreJson<ConversationLaneIdentity>(
    wasm.create_group_conversation_lane_json(
      groupId,
      environmentId,
      environmentChannelId,
      accessMode ?? undefined
    )
  );
}

export function createCommunityChannelConversationLane(
  communityId: string,
  communityChannelId: string,
  environmentId: string,
  environmentChannelId: string,
  accessMode?: string | null
): ConversationLaneIdentity {
  return parseCoreJson<ConversationLaneIdentity>(
    wasm.create_community_channel_conversation_lane_json(
      communityId,
      communityChannelId,
      environmentId,
      environmentChannelId,
      accessMode ?? undefined
    )
  );
}

export function conversationIdFromLaneId(laneId: string): string | undefined {
  return wasm.conversation_id_from_lane_id(laneId) ?? undefined;
}

export function conversationProjection(
  kind?: string | null,
  conversationId?: string | null,
  communityId?: string | null,
  currentUserId?: string | null,
  withUserId?: string | null
): ConversationProjection {
  return parseCoreJson<ConversationProjection>(
    wasm.conversation_projection_json(
      kind ?? undefined,
      conversationId ?? undefined,
      communityId ?? undefined,
      currentUserId ?? undefined,
      withUserId ?? undefined
    )
  );
}

export function laneKeyScope(laneId: string, conversationKind: string): string | undefined {
  return wasm.lane_key_scope(laneId, conversationKind) ?? undefined;
}

export function callRoomIdFromThreadKey(threadKey: string): string {
  return wasm.call_room_id(threadKey);
}

export function parseThreadId(threadKey: string): { user1: string; user2: string } | null {
  // Parse thread key format: "dm:{user1}_{user2}" where users are sorted lexicographically
  if (!threadKey.startsWith("dm:")) return null;
  const rest = threadKey.slice(3);
  const separatorIndex = rest.indexOf("_");
  if (separatorIndex === -1) return null;
  const user1 = rest.slice(0, separatorIndex);
  const user2 = rest.slice(separatorIndex + 1);
  if (!user1 || !user2) return null;
  return { user1, user2 };
}

export function isThreadId(id: string): boolean {
  return wasm.is_thread_id(id);
}

export function isGroupId(id: string): boolean {
  return wasm.is_group_id(id);
}

export function normalizeGroupIdAlias(rawGroupId: string): string | undefined {
  return wasm.normalize_group_id_alias(rawGroupId) ?? undefined;
}

export function groupIdFromThreadKey(threadKey: string): string | undefined {
  return wasm.group_id_from_thread_key(threadKey) ?? undefined;
}

export function canonicalGroupThreadKey(rawGroupId: string): string | undefined {
  return wasm.canonical_group_thread_key(rawGroupId) ?? undefined;
}

export function groupIdAliasCandidates(rawGroupId: string): string[] {
  return wasm.group_id_alias_candidates(rawGroupId) || [];
}

export function resolveGroupKeyCandidates(
  payloadKey?: string | null,
  storedCurrentKey?: string | null,
  storedMetadataKey?: string | null,
  storedHistoricalKeys: string[] = [],
  communityFallbackKey?: string | null
): string[] {
  return wasm.resolve_group_key_candidates(
    payloadKey ?? null,
    storedCurrentKey ?? null,
    storedMetadataKey ?? null,
    storedHistoricalKeys,
    communityFallbackKey ?? null
  );
}

export function threadDisplayName(
  isDm: boolean,
  isCommunity: boolean,
  threadName: string,
  communityName?: string | null,
  contactFullHandle?: string | null,
  contactUsername?: string | null,
  contactDeviceId?: string | null
): string {
  return wasm.thread_display_name(
    isDm,
    isCommunity,
    threadName,
    communityName ?? undefined,
    contactFullHandle ?? undefined,
    contactUsername ?? undefined,
    contactDeviceId ?? undefined
  );
}

/**
 * Primary display label for a contact row (handle → device id → user id →
 * "Unknown"). Single source of truth in core; shared with iOS/Android.
 */
export function contactPrimaryLabel(
  fullHandle?: string | null,
  username?: string | null,
  discriminator?: string | null,
  deviceId?: string | null,
  userId?: string | null
): string {
  return wasm.contact_primary_label(
    fullHandle ?? undefined,
    username ?? undefined,
    discriminator ?? undefined,
    deviceId ?? undefined,
    userId ?? undefined
  );
}

/**
 * Secondary label for a contact row: the @handle only when it adds information
 * the primary line lacks; never the raw user id.
 */
export function contactSecondaryLabel(
  fullHandle?: string | null,
  username?: string | null,
  discriminator?: string | null,
  deviceId?: string | null,
  userId?: string | null
): string {
  return wasm.contact_secondary_label(
    fullHandle ?? undefined,
    username ?? undefined,
    discriminator ?? undefined,
    deviceId ?? undefined,
    userId ?? undefined
  );
}

// ============================================================================
// Base64 Encoding/Decoding Utilities
// ============================================================================

/**
 * Encode a Uint8Array to base64 string
 */
export function encodeBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Decode a base64 string to Uint8Array
 */
export function decodeBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ============================================================================
// Crypto Functions - NaCl compatible encryption
// ============================================================================

/**
 * Supplies this device's signing keypair for blob writes.
 *
 * Injected rather than imported so this module does not depend on the storage
 * layer (which imports from here). Set once during init.
 */
let signingKeyProvider: (() => Promise<{ publicKey: string; secretKey: string } | undefined>) | undefined;

export function setSigningKeyProvider(
  provider: () => Promise<{ publicKey: string; secretKey: string } | undefined>
): void {
  signingKeyProvider = provider;
}

/**
 * Sign a blob write, or return undefined when this device has no signing key.
 *
 * An unsigned write is rejected by the server rather than silently accepted,
 * so failing to sign fails closed.
 */
async function signBlobWrite(
  recordId: string,
  version: number,
  encryptedData: string
): Promise<{ signature: string; signingPublicKey: string } | undefined> {
  try {
    const keys = await signingKeyProvider?.();
    if (!keys?.publicKey || !keys?.secretKey) return undefined;
    const payload = wasm.build_blob_write_payload(recordId, BigInt(version), encryptedData);
    const signature = wasm.sign(payload, decodeBase64(keys.secretKey));
    return { signature: encodeBase64(signature), signingPublicKey: keys.publicKey };
  } catch (err) {
    console.warn("[switch-core] failed to sign blob write", err);
    return undefined;
  }
}

export const crypto = {
  // Secretbox (symmetric encryption)
  secretboxKeygen: () => wasm.secretbox_keygen(),
  secretboxNonce: () => wasm.secretbox_nonce(),
  secretboxSeal: (plaintext: Uint8Array, nonce: Uint8Array, key: Uint8Array) =>
    wasm.secretbox_seal(plaintext, nonce, key),
  secretboxOpen: (ciphertext: Uint8Array, nonce: Uint8Array, key: Uint8Array) =>
    wasm.secretbox_open(ciphertext, nonce, key),

  // Box (asymmetric encryption)
  boxKeypair: () => wasm.box_keypair(),
  mlKem768Keypair: () => wasm.ml_kem_768_keypair(),
  /**
   * Ed25519 signing keypair authorizing this device's writes to encrypted
   * thread/community blobs. Distinct from boxKeypair: X25519 cannot sign.
   */
  signingKeypair: () => wasm.signing_keypair(),
  /** Sign a blob-write payload built by buildBlobWritePayload. */
  sign: (message: Uint8Array, secretKey: Uint8Array) => wasm.sign(message, secretKey),
  /** Verify a blob-write signature. */
  verifySignature: (message: Uint8Array, signature: Uint8Array, publicKey: Uint8Array) =>
    wasm.verify_signature(message, signature, publicKey),
  /**
   * Canonical bytes to sign for a blob write. Must come from core — the server
   * derives the same bytes independently, so a locally-built payload yields a
   * signature it rejects.
   */
  buildBlobWritePayload: (recordId: string, version: number, encryptedData: string) =>
    wasm.build_blob_write_payload(recordId, BigInt(version), encryptedData),
  boxKeypairFromSecret: (secretKey: Uint8Array) => wasm.box_keypair_from_secret(secretKey),
  boxNonce: () => wasm.box_nonce(),
  boxSeal: (
    plaintext: Uint8Array,
    nonce: Uint8Array,
    theirPublicKey: Uint8Array,
    mySecretKey: Uint8Array
  ) => wasm.box_seal(plaintext, nonce, theirPublicKey, mySecretKey),
  boxOpen: (
    ciphertext: Uint8Array,
    nonce: Uint8Array,
    theirPublicKey: Uint8Array,
    mySecretKey: Uint8Array
  ) => wasm.box_open(ciphertext, nonce, theirPublicKey, mySecretKey),
};

// ============================================================================
// High-level Message Encryption - Simplified APIs
// ============================================================================

export interface EncryptedMessageResult {
  encrypted_content: string;
  nonce: string;
}

/**
 * Encrypt a direct message using asymmetric encryption (NaCl box)
 * @param plaintext - The message to encrypt
 * @param senderSecretKeyBase64 - Sender's secret key (base64)
 * @param receiverPublicKeyBase64 - Receiver's public key (base64)
 * @returns { encrypted_content: string, nonce: string } - Both base64 encoded
 */
export function encryptDMMessage(
  plaintext: string,
  senderSecretKeyBase64: string,
  receiverPublicKeyBase64: string
): EncryptedMessageResult {
  const senderSecretKey = decodeBase64(senderSecretKeyBase64);
  const receiverPublicKey = decodeBase64(receiverPublicKeyBase64);
  const nonce = crypto.boxNonce();
  const plaintextBytes = new TextEncoder().encode(plaintext);
  const ciphertext = crypto.boxSeal(plaintextBytes, nonce, receiverPublicKey, senderSecretKey);
  return {
    encrypted_content: encodeBase64(ciphertext),
    nonce: encodeBase64(nonce),
  };
}

/**
 * Decrypt a direct message using asymmetric encryption (NaCl box)
 * @param encryptedContentBase64 - Encrypted content (base64)
 * @param nonceBase64 - Nonce (base64)
 * @param senderPublicKeyBase64 - Sender's public key (base64)
 * @param receiverSecretKeyBase64 - Receiver's secret key (base64)
 * @returns Decrypted plaintext string
 */
export function decryptDMMessage(
  encryptedContentBase64: string,
  nonceBase64: string,
  senderPublicKeyBase64: string,
  receiverSecretKeyBase64: string
): string {
  const ciphertext = decodeBase64(encryptedContentBase64);
  const nonce = decodeBase64(nonceBase64);
  const senderPublicKey = decodeBase64(senderPublicKeyBase64);
  const receiverSecretKey = decodeBase64(receiverSecretKeyBase64);
  const plaintext = crypto.boxOpen(ciphertext, nonce, senderPublicKey, receiverSecretKey);
  return new TextDecoder().decode(plaintext);
}

/**
 * Encrypt a group message using symmetric encryption (NaCl secretbox)
 * @param plaintext - The message to encrypt
 * @param groupKeyBase64 - Group's shared key (base64)
 * @returns { encrypted_content: string, nonce: string } - Both base64 encoded
 */
export function encryptGroupMessage(
  plaintext: string,
  groupKeyBase64: string
): EncryptedMessageResult {
  const key = decodeBase64(groupKeyBase64);
  const nonce = crypto.secretboxNonce();
  const plaintextBytes = new TextEncoder().encode(plaintext);
  const ciphertext = crypto.secretboxSeal(plaintextBytes, nonce, key);
  return {
    encrypted_content: encodeBase64(ciphertext),
    nonce: encodeBase64(nonce),
  };
}

/**
 * Decrypt a group message using symmetric encryption (NaCl secretbox)
 * @param encryptedContentBase64 - Encrypted content (base64)
 * @param nonceBase64 - Nonce (base64)
 * @param groupKeyBase64 - Group's shared key (base64)
 * @returns Decrypted plaintext string
 */
export function decryptGroupMessage(
  encryptedContentBase64: string,
  nonceBase64: string,
  groupKeyBase64: string
): string {
  const ciphertext = decodeBase64(encryptedContentBase64);
  const nonce = decodeBase64(nonceBase64);
  const key = decodeBase64(groupKeyBase64);
  const plaintext = crypto.secretboxOpen(ciphertext, nonce, key);
  return new TextDecoder().decode(plaintext);
}

// ============================================================================
// High-level Message Preparation (Cross-Platform Compatible)
// ============================================================================
// These functions handle content preparation + encryption in one step.
// Use these for cross-platform compatibility (iOS, Android, Web).

/**
 * Prepared DM message result
 */
export interface PreparedDMMessage {
  id: string;
  receiver_id: string;
  sender_key_id?: string | null;
  target_device_id?: string | null;
  encrypted_content: string;
  nonce: string;
  enc_version?: string | null;
  ml_kem_ciphertext?: string | null;
  thread_key: string;
  content_type: string;
}

/**
 * Prepared group message result
 */
export interface PreparedGroupMessage {
  id: string;
  group_id: string;
  encrypted_content: string;
  nonce: string;
  content_type: string;
  /** Key version used to encrypt this message (for key rotation support) */
  key_version: number;
}

/**
 * Prepare a DM message for sending (high-level API)
 *
 * This is the RECOMMENDED way to prepare messages for cross-platform compatibility.
 * It handles:
 * - Content preparation (raw text for TEXT/GIPHY, JSON for attachments)
 * - Encryption using asymmetric NaCl box
 * - Thread ID and message ID generation
 *
 * @param plaintext - Message content (raw text for TEXT/GIPHY, JSON string for FILE)
 * @param receiverId - Recipient's user ID
 * @param receiverPublicKeyBase64 - Recipient's public key (base64)
 * @param myUserId - Your user ID
 * @param mySecretKeyBase64 - Your secret key (base64)
 * @param contentType - Content type: "TEXT", "GIPHY", "FILE", "AUDIO", "VIDEO", "IMAGE"
 * @returns PreparedDMMessage ready for WebSocket transmission
 */
export function prepareDMMessage(
  plaintext: string,
  receiverId: string,
  receiverPublicKeyBase64: string,
  myUserId: string,
  mySecretKeyBase64: string,
  contentType: string = "TEXT"
): PreparedDMMessage {
  const result = wasm.prepare_dm_message(
    plaintext,
    receiverId,
    receiverPublicKeyBase64,
    myUserId,
    mySecretKeyBase64,
    contentType
  );
  return {
    id: result.id,
    receiver_id: result.receiver_id,
    encrypted_content: result.encrypted_content,
    nonce: result.nonce,
    thread_key: result.thread_key,
    content_type: result.content_type,
  };
}

export function prepareDMMessageForDevice(
  plaintext: string,
  receiverId: string,
  receiverDeviceId: string | null | undefined,
  receiverPublicKeyBase64: string,
  receiverMlKemPublicKeyBase64: string | null | undefined,
  myUserId: string,
  myDeviceId: string | null | undefined,
  mySecretKeyBase64: string,
  myMlKemSecretKeyBase64: string | null | undefined,
  contentType: string = "TEXT"
): PreparedDMMessage {
  const result = wasm.prepare_dm_message_for_device(
    plaintext,
    receiverId,
    receiverDeviceId || undefined,
    receiverPublicKeyBase64,
    receiverMlKemPublicKeyBase64 || undefined,
    myUserId,
    myDeviceId || undefined,
    mySecretKeyBase64,
    myMlKemSecretKeyBase64 || undefined,
    contentType
  );
  return {
    id: result.id,
    receiver_id: result.receiver_id,
    sender_key_id: result.sender_key_id || null,
    target_device_id: result.target_device_id || null,
    encrypted_content: result.encrypted_content,
    nonce: result.nonce,
    enc_version: result.enc_version || null,
    ml_kem_ciphertext: result.ml_kem_ciphertext || null,
    thread_key: result.thread_key,
    content_type: result.content_type,
  };
}

/**
 * Prepare a group message for sending (high-level API)
 *
 * This is the RECOMMENDED way to prepare group messages for cross-platform compatibility.
 * It handles:
 * - Content preparation (raw text for TEXT/GIPHY, JSON for attachments)
 * - Encryption using symmetric NaCl secretbox
 * - Message ID generation
 * - Key version tracking for rotation support
 *
 * @param plaintext - Message content (raw text for TEXT/GIPHY, JSON string for FILE)
 * @param groupId - Group/thread ID
 * @param groupKeyBase64 - Group's shared key (base64)
 * @param contentType - Content type: "TEXT", "GIPHY", "FILE", "AUDIO", "VIDEO", "IMAGE"
 * @param keyVersion - Key version used for encryption (default: 1)
 * @returns PreparedGroupMessage ready for WebSocket transmission
 */
export function prepareGroupMessage(
  plaintext: string,
  groupId: string,
  groupKeyBase64: string,
  contentType: string = "TEXT",
  keyVersion: number = 1
): PreparedGroupMessage {
  const result = wasm.prepare_group_message(plaintext, groupId, groupKeyBase64, contentType, keyVersion);
  return {
    id: result.id,
    group_id: result.group_id,
    encrypted_content: result.encrypted_content,
    nonce: result.nonce,
    content_type: result.content_type,
    key_version: result.key_version,
  };
}

// ============================================================================
// Date/Time Formatting Utilities
// ============================================================================

function toDate(timestamp: number): Date {
  return new Date(timestamp);
}

function formatHhmm(timestamp: number): string {
  const currentDate = toDate(timestamp);
  let hours = currentDate.getHours();
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
}

function formatToHuman(timestamp: number): string {
  const dateFromTimestamp = toDate(timestamp);
  const now = new Date();

  // Check if today
  const isToday =
    dateFromTimestamp.getDate() === now.getDate() &&
    dateFromTimestamp.getMonth() === now.getMonth() &&
    dateFromTimestamp.getFullYear() === now.getFullYear();

  if (isToday) {
    let hours = dateFromTimestamp.getHours();
    const minutes = String(dateFromTimestamp.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  }

  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    dateFromTimestamp.getDate() === yesterday.getDate() &&
    dateFromTimestamp.getMonth() === yesterday.getMonth() &&
    dateFromTimestamp.getFullYear() === yesterday.getFullYear();

  if (isYesterday) {
    return "Yesterday";
  }

  const year = String(dateFromTimestamp.getFullYear()).slice(-2);
  const month = String(dateFromTimestamp.getMonth() + 1).padStart(2, "0");
  const day = String(dateFromTimestamp.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
}

function formatYyyyMmDd(timestamp: number): string {
  const date = toDate(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Format timestamp as time only (e.g., "2:30 PM")
 */
export function formatTime(timestamp: number): string {
  if (!timestamp) return "";
  return formatHhmm(timestamp);
}

/**
 * Format timestamp as human-readable relative date
 * Today: "02:30 pm", Yesterday: "Yesterday", Older: "24/01/15"
 */
export function formatRelativeDate(timestamp: number): string {
  if (!timestamp) return "";
  return formatToHuman(timestamp);
}

/**
 * Format timestamp as date + time (e.g., "1/15/2024 2:30 PM")
 */
export function formatDateTime(timestamp: number): string {
  if (!timestamp) return "";
  const date = toDate(timestamp);
  return date.toLocaleDateString() + " " + formatHhmm(timestamp);
}

/**
 * Format timestamp as full locale string (e.g., "1/15/2024, 2:30:45 PM")
 */
export function formatTimestamp(timestamp: number): string {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleString();
}

/**
 * Format timestamp as ISO date (e.g., "2024-01-15")
 */
export function formatISODate(timestamp: number): string {
  if (!timestamp) return "";
  return formatYyyyMmDd(timestamp);
}

// ============================================================================
// Message Payload Building
// ============================================================================

/**
 * MessageSendPayload structure matching the WebSocket protocol
 */
export interface MessageSendPayload {
  id: string;
  receiverId: string;
  encryptedContent: string;
  nonce: string;
  contentType?: string;
  threadKey?: string;
  replyTo?: string;
}

/**
 * Build an encrypted DM message payload ready for sending over WebSocket
 * @param options - Message options
 * @returns MessageSendPayload ready for WebSocket transmission
 */
export function buildDMMessagePayload(options: {
  id?: string;
  content: string;
  receiverId: string;
  senderSecretKeyBase64: string;
  receiverPublicKeyBase64: string;
  contentType?: string;
  threadKey?: string;
  replyTo?: string;
}): MessageSendPayload {
  const messageId = options.id || generateUUID();
  const encrypted = encryptDMMessage(
    options.content,
    options.senderSecretKeyBase64,
    options.receiverPublicKeyBase64
  );

  const payload: MessageSendPayload = {
    id: messageId,
    receiverId: options.receiverId,
    encryptedContent: encrypted.encrypted_content,
    nonce: encrypted.nonce,
  };

  if (options.contentType) payload.contentType = options.contentType;
  if (options.threadKey) payload.threadKey = options.threadKey;
  if (options.replyTo) payload.replyTo = options.replyTo;

  return payload;
}

/**
 * Build an encrypted group message payload ready for sending over WebSocket
 * @param options - Message options
 * @returns MessageSendPayload ready for WebSocket transmission
 */
export function buildGroupMessagePayload(options: {
  id?: string;
  content: string;
  groupId: string;
  groupKeyBase64: string;
  contentType?: string;
  replyTo?: string;
}): MessageSendPayload {
  const messageId = options.id || generateUUID();
  const encrypted = encryptGroupMessage(options.content, options.groupKeyBase64);

  const payload: MessageSendPayload = {
    id: messageId,
    receiverId: options.groupId, // For groups, receiverId is the groupId
    encryptedContent: encrypted.encrypted_content,
    nonce: encrypted.nonce,
  };

  if (options.contentType) payload.contentType = options.contentType;
  payload.threadKey = options.groupId;
  if (options.replyTo) payload.replyTo = options.replyTo;

  return payload;
}

// ============================================================================
// User Lookup Utilities
// ============================================================================

/**
 * User lookup type based on input format
 */
export type UserLookupType = "handle" | "username" | "userId" | "deviceId";

/**
 * Detect the type of user lookup needed based on input format
 * @param input - User input string (handle, username, userId, or deviceId)
 * @returns The type of lookup to perform
 */
export function detectUserLookupType(input: string): UserLookupType {
  if (!input) return "username";

  // Check if it's a handle format (username.1234)
  const lastDotIndex = input.lastIndexOf(".");
  if (lastDotIndex !== -1) {
    const potentialDisc = input.substring(lastDotIndex + 1);
    if (/^\d{4}$/.test(potentialDisc)) {
      return "handle";
    }
  }

  // Check if it's a UUID format (userId or deviceId)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(input)) {
    return "userId"; // Could also be deviceId, but userId is more common
  }

  // Default to username search
  return "username";
}

/**
 * Parse a handle string into username and discriminator
 * @param handle - Handle in format "username.1234"
 * @returns Parsed username and discriminator, or null if invalid
 */
export function parseUserHandle(handle: string): { username: string; discriminator: string } | null {
  return wasm.parse_handle(handle);
}

/**
 * Check if a string is a valid handle format (username.1234)
 */
export function isValidHandle(input: string): boolean {
  const lastDotIndex = input.lastIndexOf(".");
  if (lastDotIndex === -1) return false;
  const potentialDisc = input.substring(lastDotIndex + 1);
  return /^\d{4}$/.test(potentialDisc) && lastDotIndex > 0;
}

// ============================================================================
// Message Content Parsing
// ============================================================================

/**
 * Parsed attachment content from a message
 */
export interface ParsedAttachment {
  id: string;
  encryptedUrl: string;
  objectUrl?: string;
  keyBase64?: string;
  filename: string;
  size: number;
  mimeType: string;
}

/**
 * Parsed GIPHY content from a message
 */
export interface ParsedGiphy {
  contentType: "GIPHY";
  content: string; // Giphy URL or ID
}

/**
 * Parsed profile keys content from a message
 */
export interface ParsedProfileKeys {
  displayPhotoKey?: string;
  coverPhotoKey?: string;
  profileKey?: string;
}

/**
 * Try to parse decrypted message content as an attachment
 * @param content - Decrypted message content (JSON string)
 * @returns Parsed attachment or null if not an attachment
 */
export function parseAttachmentContent(content: string): ParsedAttachment | null {
  try {
    const parsed = JSON.parse(content);
    const id = parsed.id ?? parsed.attachmentId ?? parsed.attachment_id;
    const encryptedUrl = parsed.encryptedUrl ?? parsed.encrypted_url ?? parsed.objectUrl ?? parsed.object_url ?? parsed.url;
    const objectUrl = parsed.objectUrl ?? parsed.object_url;
    const keyBase64 = parsed.keyBase64 ?? parsed.key_base64 ?? parsed.encryptionKey ?? parsed.encryption_key;
    const filename = parsed.filename ?? parsed.fileName ?? parsed.file_name;
    const size = parsed.size;
    const mimeType = parsed.mimeType ?? parsed.mime_type ?? parsed.contentType ?? parsed.content_type;
    if (
      typeof id === "string" &&
      typeof encryptedUrl === "string" &&
      typeof filename === "string" &&
      typeof size === "number" &&
      typeof mimeType === "string"
    ) {
      return {
        id,
        encryptedUrl,
        objectUrl,
        keyBase64,
        filename,
        size,
        mimeType,
      };
    }
  } catch {
    // Not JSON, not an attachment
  }
  return null;
}

/**
 * Try to parse decrypted message content as GIPHY
 * @param content - Decrypted message content (JSON string)
 * @returns Parsed GIPHY or null if not a GIPHY message
 */
export function parseGiphyContent(content: string): ParsedGiphy | null {
  try {
    const parsed = JSON.parse(content);
    if (parsed.contentType === "GIPHY" && typeof parsed.content === "string") {
      return {
        contentType: "GIPHY",
        content: parsed.content,
      };
    }
  } catch {
    // Not JSON, not a GIPHY
  }
  return null;
}

/**
 * Try to parse decrypted message content as profile keys
 * @param content - Decrypted message content (JSON string)
 * @returns Parsed profile keys or null if not profile keys
 */
export function parseProfileKeysContent(content: string): ParsedProfileKeys | null {
  try {
    const parsed = JSON.parse(content);
    if (
      parsed.displayPhotoKey !== undefined ||
      parsed.coverPhotoKey !== undefined ||
      parsed.profileKey !== undefined
    ) {
      return {
        displayPhotoKey: parsed.displayPhotoKey,
        coverPhotoKey: parsed.coverPhotoKey,
        profileKey: parsed.profileKey,
      };
    }
  } catch {
    // Not JSON or not profile keys
  }
  return null;
}

/**
 * Detected message content type
 */
export type MessageContentType = "TEXT" | "ATTACHMENT" | "GIPHY" | "PROFILE_KEYS" | "CALL" | "UNKNOWN";

/**
 * Detect the content type of a decrypted message
 * @param content - Decrypted message content
 * @param contentTypeHint - Optional content type hint from message metadata
 * @returns Detected content type
 */
export function detectMessageContentType(content: string, contentTypeHint?: string): MessageContentType {
  // If we have a hint, use it
  if (contentTypeHint) {
    if (contentTypeHint === "GIPHY") return "GIPHY";
    if (contentTypeHint === "FILE" || contentTypeHint === "ATTACHMENT") return "ATTACHMENT";
    if (contentTypeHint === "CALL") return "CALL";
    if (contentTypeHint === "PROFILE_KEYS") return "PROFILE_KEYS";
  }

  // Try to detect from content
  if (parseAttachmentContent(content)) return "ATTACHMENT";
  if (parseGiphyContent(content)) return "GIPHY";
  if (parseProfileKeysContent(content)) return "PROFILE_KEYS";

  // Default to TEXT
  return "TEXT";
}

// ============================================================================
// WebSocket Event Types
// ============================================================================

export function parseEventType(eventType: string): string | null {
  return wasm.parse_event_type(eventType);
}

// ============================================================================
// JWT Parsing - Centralized claim extraction via Rust core
// ============================================================================

/**
 * JWT payload structure matching core's JwtPayload (snake_case)
 */
export interface CoreJwtPayload {
  user_id?: string;
  device_id?: string;
  app_id?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

/**
 * Parse JWT payload without verification
 * Handles base64url decoding and padding correctly via Rust core
 * @param token - JWT token string
 * @returns Parsed payload or null if invalid
 */
export function parseJwtPayload(token: string): CoreJwtPayload | null {
  return wasm.parse_jwt_payload(token);
}

// ============================================================================
// Attachment Functions - Encrypt/decrypt file attachments
// ============================================================================

export const attachments = {
  /** Encrypt a file for sending. Returns { data: Uint8Array, key_base64: string } */
  encrypt: (plaintext: Uint8Array) => wasm.attachment_encrypt(plaintext),

  /** Decrypt a file. Takes encrypted data and base64 key */
  decrypt: (encryptedData: Uint8Array, keyBase64: string) =>
    wasm.attachment_decrypt(encryptedData, keyBase64),

  /** Build encrypted URL by appending key to base URL */
  buildUrl: (baseUrl: string, keyBase64: string) =>
    wasm.attachment_build_url(baseUrl, keyBase64),

  /** Parse encrypted URL to get [baseUrl, keyBase64] */
  parseUrl: (encryptedUrl: string): [string, string] =>
    wasm.attachment_parse_url(encryptedUrl) as [string, string],

  /** Extract just the key from encrypted URL */
  extractKey: (encryptedUrl: string): string | undefined =>
    wasm.attachment_extract_key(encryptedUrl),

  /** Extract just the base URL from encrypted URL */
  extractBaseUrl: (encryptedUrl: string): string | undefined =>
    wasm.attachment_extract_base_url(encryptedUrl),
};

// ============================================================================
// WebSocket Client - Real-time messaging
// ============================================================================

export type WebSocketEventCallback = (eventJson: string) => void;

export const ws = {
  /** Create a new WebSocket client */
  create: (url: string, token?: string) => wasm.ws_create(url, token),

  /** Set event callback. Receives JSON string with { type, payload } */
  onEvent: (callback: WebSocketEventCallback) => wasm.ws_on_event(callback),

  /** Connect to WebSocket server */
  connect: () => wasm.ws_connect(),

  /** Check if connected */
  isConnected: () => wasm.ws_is_connected(),

  /** Send raw message */
  sendRaw: (message: string) => wasm.ws_send_raw(message),

  /** Send encrypted chat message */
  sendMessage: (
    messageId: string,
    receiverId: string,
    encryptedContent: string,
    nonce: string,
    threadKey?: string
  ) => wasm.ws_send_message(messageId, receiverId, encryptedContent, nonce, threadKey),

  /** Acknowledge received message */
  ackMessage: (messageId: string) => wasm.ws_ack_message(messageId),

  /** Send pong response */
  pong: (timestamp: number) => wasm.ws_pong(timestamp),

  /** Default WebSocket config as JSON */
  defaultConfigJson: () => wasm.ws_default_config_json(),

  /** Disconnect from server */
  disconnect: () => {
    console.log("[switch-core/index.ts] ws.disconnect() called");
    try {
      const result = wasm.ws_disconnect();
      console.log("[switch-core/index.ts] ws_disconnect result:", result);
    } catch (e) {
      console.error("[switch-core/index.ts] ws_disconnect error:", e);
    }
  },

  /** Destroy the client */
  destroy: () => wasm.ws_destroy(),
};

// ============================================================================
// API Client - HTTP API calls
// ============================================================================

export const api = {
  /** Configure API client with base URL and optional token */
  configure: (baseUrl: string, token?: string) => wasm.api_configure(baseUrl, token),

  /** Update auth token */
  setToken: (token?: string) => wasm.api_set_token(token),

  /** Register new device */
  register: (username: string, appId: string, publicKey: string) =>
    wasm.api_register(username, appId, publicKey),

  /** Register new device with optional ML-KEM public key */
  registerWithMlKem: (
    username: string,
    appId: string,
    publicKey: string,
    mlKemPublicKey?: string
  ) => wasm.api_register_with_ml_kem(username, appId, publicKey, mlKemPublicKey),

  /** Register new user (requires auth) */
  registerUser: (username: string) => wasm.api_register_user(username),

  /** Register user with discriminator */
  registerUserWithDiscriminator: (username: string) =>
    wasm.api_register_user_with_discriminator(username),

  /** Find user by handle */
  findUser: (handle: string) => wasm.api_find_user(handle),

  /** Search user by username */
  searchUser: (username: string) => wasm.api_search_user(username),

  /** Search user by ID */
  searchUserId: (userId: string) => wasm.api_search_user_id(userId),

  /** Set display photo (requires auth) */
  setDisplayPhoto: (url: string) => wasm.api_set_display_photo(url),

  /** Set display photo with canonical media metadata (requires auth) */
  setDisplayPhotoWithMedia: (displayPhotoMedia: { url: string; keyBase64?: string; mimeType?: string }) =>
    wasm.api_set_display_photo_with_media(displayPhotoMedia),

  /** Get presigned URL for file upload */
  getPresignedUrl: (filename: string) =>
    wasm.api_get_presigned_url(filename),

  /** Get cache paths for a display photo */
  displayPhotoCachePaths: (userId: string, cleanUrl: string) =>
    wasm.display_photo_cache_paths(userId, cleanUrl),

  /** Remove an object from storage */
  removeObject: (key: string) => wasm.api_remove_object(key),

  /** Prepare encrypted contact-add payload (shares display photo key/url) */
  prepareContactAddMessage: (
    userId: string,
    displayPhotoUrl: string | null | undefined,
    displayPhotoKey: string | null | undefined,
    receiverId: string,
    receiverPublicKeyBase64: string,
    myUserId: string,
    mySecretKeyBase64: string
  ) =>
    wasm.prepare_contact_add_message(
      userId,
      displayPhotoUrl,
      displayPhotoKey,
      receiverId,
      receiverPublicKeyBase64,
      myUserId,
      mySecretKeyBase64
    ),

  /** Prepare a display photo key fanout payload (DM encrypted) */
  prepareDisplayPhotoKeyMessage: (
    userId: string,
    displayPhotoUrl: string,
    displayPhotoKey: string,
    receiverId: string,
    receiverPublicKeyBase64: string,
    myUserId: string,
    mySecretKeyBase64: string
  ) =>
    (wasm as any).prepare_display_photo_key_message(
      userId,
      displayPhotoUrl,
      displayPhotoKey,
      receiverId,
      receiverPublicKeyBase64,
      myUserId,
      mySecretKeyBase64
    ),

  /** Prepare display photo fanout payload (alias) */
  prepareDisplayPhotoFanout: (
    userId: string,
    displayPhotoUrl: string,
    displayPhotoKey: string,
    receiverId: string,
    receiverPublicKeyBase64: string,
    myUserId: string,
    mySecretKeyBase64: string
  ) =>
    (wasm as any).prepare_display_photo_fanout(
      userId,
      displayPhotoUrl,
      displayPhotoKey,
      receiverId,
      receiverPublicKeyBase64,
      myUserId,
      mySecretKeyBase64
    ),

  /** Parse display photo update content */
  parseDisplayPhotoUpdate: (content: string) => (wasm as any).parse_display_photo_update(content),

  /** Fetch messages */
  fetchMessages: () => wasm.api_fetch_messages(),

  /** Get group by ID (with retry and exponential backoff) */
  getGroup: async (groupId: string) => {
    const maxRetries = 3;
    const initialDelayMs = 1000;
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await wasm.api_get_group(groupId);
      } catch (err) {
        lastError = err;
        console.warn(
          `[api.getGroup] Failed (attempt ${attempt}/${maxRetries}):`,
          err
        );
        if (attempt < maxRetries) {
          const delay = initialDelayMs * Math.pow(2, attempt - 1); // 1s, 2s, 4s
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    }
    throw lastError;
  },

  /** Create or update a group (requires auth, takes EncryptedThread) */
  createOrUpdateGroup: async (encryptedThread: EncryptedThreadType, writerKeys?: string[]) => {
    const signed = await signBlobWrite(
      (encryptedThread as any).threadKey ?? (encryptedThread as any).thread_key ?? "",
      Number((encryptedThread as any).version ?? 0),
      (encryptedThread as any).encryptedData ?? (encryptedThread as any).encrypted_data ?? ""
    );
    return wasm.api_create_or_update_group(
      encryptedThread,
      signed?.signature,
      signed?.signingPublicKey,
      writerKeys
    );
  },

  /** Get community by ID */
  getCommunity: (communityId: string) => wasm.api_get_community(communityId),

  /** Create or update a community (requires auth, takes EncryptedCommunity) */
  createOrUpdateCommunity: async (encryptedCommunity: any, writerKeys?: string[]) => {
    const signed = await signBlobWrite(
      encryptedCommunity?.communityId ?? encryptedCommunity?.community_id ?? "",
      Number(encryptedCommunity?.version ?? 0),
      encryptedCommunity?.encryptedData ?? encryptedCommunity?.encrypted_data ?? ""
    );
    return wasm.api_create_or_update_community(
      encryptedCommunity,
      signed?.signature,
      signed?.signingPublicKey,
      writerKeys
    );
  },

  /** List user communities (requires auth) */
  listUserCommunities: (userId: string | null) => wasm.api_list_user_communities(userId),

  /** Leave a community (requires auth) */
  leaveCommunity: (communityId: string) => wasm.api_leave_community(communityId),

  /** Add a member to a community (requires auth) */
  addCommunityMember: (communityId: string, userId: string) => wasm.api_add_community_member(communityId, userId),

  /** Join a community group (requires auth) */
  joinCommunityGroup: (communityId: string, groupId: string) => wasm.api_join_community_group(communityId, groupId),

  /** Delete a community */
  deleteCommunity: (communityId: string) => wasm.api_delete_community(communityId),

  /** Get call token for room */
  getCallToken: (roomName: string) => wasm.api_get_call_token(roomName),

  // Thread member management (aligned naming)
  /** Get thread members (requires auth) */
  getThreadMembers: (threadKey: string) => wasm.api_get_group_members(threadKey),

  /** Set thread members (requires auth) */
  setThreadMembers: (threadKey: string, members: any) => wasm.api_set_group_members(threadKey, members),

  /** Add a member to a thread (requires auth) */
  addThreadMember: (threadKey: string, userId: string, role?: string | null) => wasm.api_add_group_member(threadKey, userId, role),

  /** Remove a member from a thread (requires auth) */
  removeThreadMember: (threadKey: string, userId: string) => wasm.api_remove_group_member(threadKey, userId),

  // Community member management
  /** Get community members (requires auth) */
  getCommunityMembers: (communityId: string) => wasm.api_get_community_members(communityId),

  /** Set community members (requires auth) */
  setCommunityMembers: (communityId: string, members: any) => wasm.api_set_community_members(communityId, members),

  /** Remove a member from a community (requires auth) */
  removeCommunityMember: (communityId: string, userId: string) => wasm.api_remove_community_member(communityId, userId),

  // Legacy aliases for backward compatibility
  /** @deprecated Use getThreadMembers instead */
  getGroupMembers: (groupId: string) => wasm.api_get_group_members(groupId),

  /** @deprecated Use addThreadMember instead */
  addGroupMember: (groupId: string, userId: string, role?: string | null) => wasm.api_add_group_member(groupId, userId, role),

  /** @deprecated Use removeThreadMember instead */
  removeGroupMember: (groupId: string, userId: string) => wasm.api_remove_group_member(groupId, userId),
};

// ============================================================================
// Groups - High-level group management
// ============================================================================

export const groups = {
  /** Create a new group with generated key, encrypt, and sync to API
   *
   * This high-level function:
   * 1. Creates a Thread with a generated symmetric group key
   * 2. Encrypts the thread data
   * 3. Syncs to the server API
   *
   * @param name - Group name (1-100 chars)
   * @param description - Optional description (max 500 chars)
   * @param ownerId - Owner's user ID
   * @returns The created Thread object (with the group key)
   */
  createGroup: (
    name: string,
    description: string | undefined | null,
    ownerId: string
  ): Promise<ThreadType> => wasm.create_group(name, description, ownerId),

  /** Create a group thread locally without syncing to API
   *
   * Returns the Thread object with a generated group key.
   * Use this when you want to create a group offline or handle syncing separately.
   */
  createGroupThread: (
    name: string,
    description: string | undefined | null,
    ownerId: string
  ): ThreadType => wasm.create_group_thread(name, description, ownerId),

  /** Encrypt a Thread to create an EncryptedThread for API
   *
   * Use this when you have a Thread object and need to sync it to the server.
   */
  encryptThreadForApi: (thread: ThreadType): EncryptedThreadType => wasm.encrypt_thread_for_api(thread),

  /**
   * Prepare a USER_ADDED message to send to a new group member.
   *
   * This function is called by the admin/owner who is adding the member.
   * It encrypts the group keys and metadata using NaCl box with the recipient's public key.
   *
   * @param threadJson - JSON serialized Thread object
   * @param newMemberId - User ID of the new member
   * @param newMemberPublicKeyBase64 - New member's X25519 public key (Base64)
   * @param senderId - User ID of the admin sending this message
   * @param senderSecretKeyBase64 - Admin's X25519 secret key (Base64)
   * @returns UserAddedPayload ready to send via WebSocket
   */
  prepareUserAddedMessage: (
    threadJson: string,
    newMemberId: string,
    newMemberPublicKeyBase64: string,
    senderId: string,
    senderSecretKeyBase64: string
  ): {
    id: string;
    group_id: string;
    receiver_id: string;
    sender_id: string;
    encrypted_keys: string;
    encrypted_group_data: string;
    nonce: string;
    timestamp: number;
  } => {
    const payload = wasm.prepare_user_added_message(
      threadJson,
      newMemberId,
      newMemberPublicKeyBase64,
      senderId,
      senderSecretKeyBase64
    );
    return {
      id: payload.id,
      group_id: payload.group_id,
      receiver_id: payload.receiver_id,
      sender_id: payload.sender_id,
      encrypted_keys: payload.encrypted_keys,
      encrypted_group_data: payload.encrypted_group_data,
      nonce: payload.nonce,
      timestamp: Number(payload.timestamp),
    };
  },

  /**
   * Process a received USER_ADDED message to extract group keys and metadata.
   *
   * This function is called by the new member when they receive a USER_ADDED message.
   * It decrypts the group keys and metadata using NaCl box.
   *
   * @param payloadJson - JSON string of the received USER_ADDED payload
   * @param recipientSecretKeyBase64 - New member's X25519 secret key (Base64)
   * @param senderPublicKeyBase64 - Admin's X25519 public key (Base64)
   * @returns Decrypted group keys and metadata
   */
  processUserAddedMessage: (
    payloadJson: string,
    recipientSecretKeyBase64: string,
    senderPublicKeyBase64: string
  ): {
    groupId: string;
    groupKeys: Record<number, string>;
    groupName: string;
    groupDescription: string | null;
    currentKeyVersion: number;
  } => {
    const result = wasm.process_user_added_message(
      payloadJson,
      recipientSecretKeyBase64,
      senderPublicKeyBase64
    );
    // Convert snake_case from Rust to camelCase for JS
    return {
      groupId: result.group_id,
      groupKeys: result.group_keys,
      groupName: result.group_name,
      groupDescription: result.group_description ?? null,
      currentKeyVersion: result.current_key_version,
    };
  },

  /** Encrypt a Thread without members (for server blob upload) */
  encryptThreadForApiWithoutMembers: (thread: ThreadType): EncryptedThreadType =>
    wasm.encrypt_thread_for_api_without_members(thread),

  /** Decrypt an EncryptedThread back to a Thread using the group key
   *
   * @param encrypted - The EncryptedThread object from the server
   * @param groupKeyBase64 - The group's symmetric key (Base64)
   * @returns Decrypted Thread object
   */
  decryptThread: (encrypted: EncryptedThreadType, groupKeyBase64: string): ThreadType =>
    (wasm as any).decrypt_thread(encrypted, groupKeyBase64),

  /** Rotate the group key, generating a new version
   *
   * @param thread - The current Thread object
   * @returns Object with updated thread and rotation info
   */
  rotateGroupKey: (
    thread: ThreadType
  ): {
    thread: ThreadType;
    rotation: { newKey: string; newVersion: number; metadataKey: string };
  } => (wasm as any).rotate_group_key(thread),

  /** Get keys to distribute to a new member
   *
   * Returns a map of version -> base64 key.
   * Includes version 1 (metadata) and current version (new messages).
   *
   * @param thread - The Thread object with group keys
   * @returns Map of key version to base64-encoded key
   */
  getKeysForNewMember: (thread: ThreadType): Record<number, string> =>
    (wasm as any).get_keys_for_new_member(thread),

  /** Get the current key for message encryption
   *
   * @param thread - The Thread object
   * @returns Object with key (base64) and version number
   */
  getCurrentMessageKey: (thread: ThreadType): { key: string; version: number } =>
    (wasm as any).get_current_message_key(thread),

  /** Get a specific key version for message decryption
   *
   * @param thread - The Thread object
   * @param version - The key version to retrieve
   * @returns Base64-encoded key for that version
   */
  getKeyByVersion: (thread: ThreadType, version: number): string =>
    (wasm as any).get_key_by_version(thread, version),
};

// ============================================================================
// Auth - Authentication and session management
// ============================================================================

export const auth = {
  /** Logout - clears all local data and disconnects
   *
   * This function:
   * - Clears all data from encrypted storage
   * - Disconnects and destroys WebSocket connection
   * - Clears the API auth token
   */
  logout: (): Promise<void> => wasm.logout(),
};

// ============================================================================
// Recovery - BIP39 mnemonic-based account recovery
// ============================================================================

export const recovery = {
  /** Derive a keypair from a BIP39 mnemonic phrase using PBKDF2
   *
   * Uses PBKDF2-HMAC-SHA256 with 100,000 iterations, matching Web Crypto API.
   * Returns { public_key: Uint8Array, secret_key: Uint8Array }
   */
  deriveKeypair: (mnemonic: string): { public_key: Uint8Array; secret_key: Uint8Array } =>
    wasm.recovery_derive_keypair(mnemonic),

  /** Validate mnemonic format (12 or 24 words) */
  validateMnemonic: (mnemonic: string): boolean =>
    wasm.recovery_validate_mnemonic(mnemonic),

  /** Verify a recovery_announce public key against the server's recorded
   * pubkey for the announcing user. Used by peer-assisted recovery to drop
   * forged announces. See core/src/recovery.rs::verify_recovery_announce_pubkey.
   */
  verifyAnnouncePubkey: (announcedKey: string, recordedKey: string): boolean =>
    wasm.recovery_verify_announce_pubkey(announcedKey, recordedKey),

  /** Generate a new 12-word BIP39 mnemonic phrase
   *
   * Uses cryptographically secure random entropy to generate a valid
   * BIP39 mnemonic that can be used for account recovery.
   */
  generateMnemonic: (): string => wasm.recovery_generate_mnemonic(),

  /** Derive the recovery ID from a mnemonic
   *
   * The recovery ID is the base64-encoded public key derived from the mnemonic.
   * This is used as the key to store/retrieve backups on the server.
   */
  deriveRecoveryId: (mnemonic: string): string =>
    wasm.recovery_derive_recovery_id(mnemonic),

  /** Encrypt backup data using the recovery keypair
   *
   * Takes a mnemonic and JSON data string, returns { encryptedData, nonce, recoveryId }
   */
  encryptBackup: (mnemonic: string, dataJson: string): { encryptedData: string; nonce: string; recoveryId: string } =>
    wasm.recovery_encrypt_backup(mnemonic, dataJson),

  /** Decrypt backup data using the recovery mnemonic
   *
   * Takes encrypted data and nonce, returns the decrypted JSON string
   */
  decryptBackup: (mnemonic: string, encryptedData: string, nonce: string): string =>
    wasm.recovery_decrypt_backup(mnemonic, encryptedData, nonce),

  /** Store backup on server */
  storeBackup: (recoveryId: string, encryptedData: string, nonce: string): Promise<{ success: boolean }> =>
    wasm.api_store_recovery_backup(recoveryId, encryptedData, nonce),

  /** Get backup from server */
  getBackup: (recoveryId: string, appId: string): Promise<{ success: boolean; encrypted_data?: string; nonce?: string }> =>
    wasm.api_get_recovery_backup(recoveryId, appId),

  /**
   * Restore session from recovery backup.
   *
   * Takes the mnemonic (never the raw recovery_id): core derives the
   * recovery_id, runs the challenge/verify proof-of-possession round trip, and
   * only the decrypted challenge plaintext leaves the device. The mnemonic is
   * never transmitted.
   */
  restoreSession: (
    mnemonic: string,
    appId: string
  ): Promise<{
    success: boolean;
    token?: string;
    user_id?: string;
    username?: string;
    discriminator?: string;
    device_id?: string;
    primary_device_id?: string;
    message?: string;
  }> => wasm.api_restore_session(mnemonic, appId),

  /** Delete backup from server */
  deleteBackup: (recoveryId: string): Promise<{ success: boolean }> =>
    wasm.api_delete_recovery_backup(recoveryId),
};

// ============================================================================
// Profile / Display Photo
// ============================================================================

export const profile = {
  /** Encrypt a display photo and get presigned URL + key info */
  prepareDisplayPhotoUpload: (photo: Uint8Array) =>
    wasm.prepare_display_photo_upload(photo),
};

// ============================================================================
// HiLo Lock Screen - Dual-environment lock codes
// ============================================================================

/**
 * HiLo environment type
 */
export type HiLoEnvironment = "hi" | "lo";

/**
 * HiLo codes manager - manages hi/lo/destroy codes
 * Codes are hashed and can be verified securely
 */
export class HiLoCodes {
  private inner: any;

  constructor() {
    this.inner = new wasm.WasmHiLoCodes();
  }

  /** Check if codes are configured (at least hi and lo) */
  isConfigured(): boolean {
    return this.inner.is_configured();
  }

  /** Set codes from plaintext (will be hashed). Min 4 characters each */
  setCodes(hi: string, lo: string, destroy?: string): void {
    this.inner.set_codes(hi, lo, destroy ?? null);
  }

  /** Verify a code and return which environment it unlocks ("hi" or "lo")
   * @throws Error if code is invalid or triggers destroy
   */
  verifyCode(code: string): HiLoEnvironment {
    return this.inner.verify_code(code) as HiLoEnvironment;
  }

  /** Clear all codes */
  clear(): void {
    this.inner.clear();
  }

  /** Serialize to JSON for storage */
  toJSON(): string {
    return this.inner.to_json();
  }

  /** Deserialize from JSON */
  static fromJSON(json: string): HiLoCodes {
    const codes = new HiLoCodes();
    codes.inner = wasm.WasmHiLoCodes.from_json(json);
    return codes;
  }
}

/**
 * HiLo state manager - manages lock screen state
 */
export class HiLoState {
  private inner: any;

  constructor() {
    this.inner = new wasm.WasmHiLoState();
  }

  /** Get current environment ("hi" or "lo") */
  get environment(): HiLoEnvironment {
    return this.inner.environment() as HiLoEnvironment;
  }

  /** Check if screen is locked */
  get isLocked(): boolean {
    return this.inner.is_locked();
  }

  /** Check if codes are configured */
  get isConfigured(): boolean {
    return this.inner.is_configured();
  }

  /** Check if lock screen should be shown */
  shouldLock(): boolean {
    return this.inner.should_lock();
  }

  /** Lock the screen */
  lock(): void {
    this.inner.lock();
  }

  /** Unlock with a code, returns the environment
   * @throws Error if code is invalid or triggers destroy
   */
  unlock(code: string): HiLoEnvironment {
    return this.inner.unlock(code) as HiLoEnvironment;
  }

  /** Set the current environment directly ("hi" or "lo") */
  setEnvironment(env: HiLoEnvironment): void {
    this.inner.set_environment(env);
  }

  /** Configure codes (min 4 characters each) */
  setCodes(hi: string, lo: string, destroy?: string): void {
    this.inner.set_codes(hi, lo, destroy ?? null);
  }

  /** Serialize to JSON for storage */
  toJSON(): string {
    return this.inner.to_json();
  }

  /** Deserialize from JSON */
  static fromJSON(json: string): HiLoState {
    const state = new HiLoState();
    state.inner = wasm.WasmHiLoState.from_json(json);
    return state;
  }
}

/** Hash a HiLo code (for custom verification) */
export function hashHiLoCode(code: string): string {
  return wasm.hilo_hash_code(code);
}

// Re-export raw wasm module for advanced usage
export { wasm };

// ============================================================================
// Extensions - Platform-agnostic prototype extensions
// ============================================================================

// Import extensions to register them on String and Number prototypes
import "./extensions";

// Re-export for explicit imports if needed
export * from "./extensions";

// ============================================================================
// Utility Functions (previously in natschatapi/utils.ts)
// ============================================================================

/**
 * Creates a test user ID for development purposes
 * @returns A test user ID string
 */
export function createTestUserId(): string {
  return `test-user-${generateRandomId().slice(0, 6)}`;
}

/**
 * Creates a test app ID for development purposes
 * @returns A test app ID string
 */
export function createTestAppId(): string {
  return `test-app-${generateRandomId().slice(0, 6)}`;
}

/**
 * Validates an email address format
 * @param email - Email address to validate
 * @returns Boolean indicating if the email is valid
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Deep clones an object
 * @param obj - Object to clone
 * @returns Deep cloned object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Debounces a function
 * @param func - Function to debounce
 * @param wait - Time to wait in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttles a function
 * @param func - Function to throttle
 * @param limit - Time limit in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  return function (...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// ============================================================================
// Token Management (previously in natschatapi/core.ts and auth.ts)
// ============================================================================

/** Token stored in module for API calls */
let _authToken: string | null = null;

/** API URL (deprecated - configure via api.configure instead) */
let _apiUrl: string = "";

/**
 * Set API URL (deprecated - use api.configure instead)
 * @deprecated Configure base URL via api.configure() instead
 */
export function setApiUrl(url: string): void {
  _apiUrl = url;
  api.configure(url, _authToken || undefined);
}

/**
 * Get the current API URL
 * @returns The configured API URL
 */
export function getApiUrl(): string {
  return _apiUrl;
}

/**
 * Set the authentication token
 * @param token - JWT token to use for authenticated API calls
 */
export function setToken(token: string | null): void {
  _authToken = token;
  if (token) {
    api.setToken(token);
  }
}

/**
 * Get the current authentication token
 * @returns The current token or null if not set
 */
export function getToken(): string | null {
  return _authToken;
}

/**
 * Update token and reconnect WebSocket
 * @param newToken - New JWT token
 */
export async function updateTokenAndReconnect(newToken: string): Promise<void> {
  setToken(newToken);
  // WebSocket reconnection would be handled by the caller
}

/**
 * Decode JWT token to get payload
 * @param token - Optional token to decode (uses stored token if not provided)
 * @returns Decoded token payload or null if invalid
 */
export function decodeToken(token?: string): CoreJwtPayload | null {
  const tokenToDecode = token || _authToken;
  if (!tokenToDecode) return null;
  return parseJwtPayload(tokenToDecode);
}

/**
 * Get current user ID from token
 * @returns User ID or null if not authenticated
 */
export function getCurrentUserId(): string | null {
  const payload = decodeToken();
  return payload?.device_id || null;
}

/**
 * Get current app ID from token
 * @returns App ID or null if not authenticated
 */
export function getCurrentAppId(): string | null {
  const payload = decodeToken();
  return payload?.app_id || null;
}

// ============================================================================
// WebSocket Helpers (previously in natschatapi/index.ts)
// ============================================================================

/**
 * Connect WebSocket with current token
 * @returns Promise that resolves when connected
 */
export async function connectWebSocket(): Promise<void> {
  await initSwitchCore();
  const token = getToken();
  if (!token) throw new Error("No token available for WebSocket connection");
  // Dynamic import to avoid circular dependency issues
  const { appConfig } = await import("@/app/app-config");
  ws.create(appConfig.websocketUrl, token);
  return ws.connect();
}

/**
 * Disconnect and destroy WebSocket connection
 */
export function disconnectWebSocket(): void {
  console.log("[switch-core] disconnectWebSocket() called");
  try {
    ws.disconnect();
  } catch (e) {
    console.warn("[switch-core] ws.disconnect failed:", e);
  }
  try {
    ws.destroy();
  } catch (e) {
    console.warn("[switch-core] ws.destroy failed:", e);
  }
}

/**
 * Send a typed WebSocket message
 * @param type - Message type
 * @param payload - Message payload
 */
export function sendWebSocketMessage(type: string, payload: any): void {
  const messageStr = JSON.stringify({ type, payload });
  ws.sendRaw(messageStr);
}

/**
 * Check if WebSocket is connected
 * @returns true if connected
 */
export function isWebSocketConnected(): boolean {
  return ws.isConnected();
}

/**
 * Initialize WebSocket (alias for connectWebSocket)
 */
export function initializeWebSocket(): Promise<void> {
  return connectWebSocket();
}
